<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{
      'invoice-details-popup-overlay': true,
      'is-visible': isVisible
    }">
    <div :class="{
      'invoice-details-popup': true,
      'is-loading': isLoading
    }">
      <h2 v-if="!isLoading">
        <template v-if="dataToDisplay.type === 'fvat'">
        {{ $t('invoiceDetailsPopup.titleInvoice', { number: dataToDisplay.number }) }}
        </template>

        <template v-if="dataToDisplay.type === 'receipt'">
        {{ $t('invoiceDetailsPopup.titleReceipt', { number: dataToDisplay.number }) }}
        </template>
      </h2>

      <dl class="invoice-details-popup-content">
        <dt>{{ $t('invoiceDetailsPopup.issueDate') }}:</dt>
        <dd>{{ dataToDisplay.issue_date }}</dd>
        <dt>{{ $t('invoiceDetailsPopup.paymentDate') }}:</dt>
        <dd>{{ dataToDisplay.payment_date }}</dd>

        <dt>{{ $t('invoiceDetailsPopup.paymentType') }}:</dt>
        <dd v-if="paymentMethodName[dataToDisplay.payment_type]">{{ paymentMethodName[dataToDisplay.payment_type] }}</dd>
        <dd v-else>{{ dataToDisplay.payment_type }}</dd>

        <dt>{{ $t('invoices.isCompany') }}</dt>
        <dd>
          <template v-if="dataToDisplay.is_company">
            {{ $t('invoices.listItem.company') }}
          </template>
          <template v-else>
            {{ $t('invoices.listItem.customer') }}
          </template>
        </dd>

        <dt>{{ $t('invoiceDetailsPopup.companyTitle') }}:</dt>
        <dd>{{ dataToDisplay.company_title }}</dd>
        <dt>{{ $t('invoiceDetailsPopup.address') }}:</dt>
        <dd>{{ dataToDisplay.address }}</dd>
        <dd>{{ dataToDisplay.post_code }} {{ dataToDisplay.city }}</dd>
        <dt>{{ $t('invoiceDetailsPopup.nip') }}:</dt>
        <dd>{{ dataToDisplay.nip }}</dd>

        <dt>{{ $t('invoiceDetailsPopup.amount') }}:</dt>
        <dd>{{ currencyUtils.getQuote(dataToDisplay.amount, dataToDisplay.currency) }}</dd>
        <dt>{{ $t('invoiceDetailsPopup.vat') }}:</dt>
        <dd>{{ currencyUtils.getQuote(dataToDisplay.vat, dataToDisplay.currency) }}</dd>

        <dt v-if="dataToDisplay && dataToDisplay.notes">{{ $t('invoiceDetailsPopup.notes') }}:</dt>
        <dd v-if="dataToDisplay && dataToDisplay.notes">{{ dataToDisplay.notes }}</dd>
      </dl>

      <table class="invoice-details-popup-items">
        <thead>
          <tr>
            <td>{{ $t('invoiceDetailsPopup.name') }}</td>
            <td>{{ $t('invoiceDetailsPopup.qty') }}</td>
            <td>{{ $t('invoiceDetailsPopup.itemVAT') }}</td>
            <td>
              <template v-if="dataToDisplay.type === 'fvat'">
              {{ $t('invoiceDetailsPopup.onInvoice') }}
              </template>
              <template v-if="dataToDisplay.type === 'receipt'">
              {{ $t('invoiceDetailsPopup.onReceipt') }}
              </template>
            </td>
            <td>{{ $t('invoiceDetailsPopup.itemAmount') }}</td>
            <td>{{ $t('invoiceDetailsPopup.itemTotal') }}</td>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) of dataToDisplay.items"
            :key="'invoice-details-item-' + index">
            <td>
              <template v-if="item.inquiry_id">
                <a
                  href="#"
                  @click.prevent="$bus.$emit('show-reservation-details-popup', item.inquiry_id)">
                  {{ item.name }} (#{{ item.inquiry_id }})
                </a>
              </template>
              <template v-else>
                {{ item.name }}
              </template>
              <br>
              <small>
                <strong>{{ $t('invoiceDetailsPopup.itemPrice') }}:</strong>
                {{ currencyUtils.getQuote(item.item_price, item.currency) }}
              </small>
              <template v-if="item.gtu">
                <br>
                <small>
                  <strong>{{ $t('invoiceDetailsPopup.gtu') }}:</strong>
                  {{ item.gtu }}
                </small>
              </template>
              <template v-if="item.payment_status">
                <br>
                <small>
                  <strong>{{ $t('invoiceDetailsPopup.paymentStatus') }}:</strong>

                  <span v-if="item.payment_status === 'COMPLETED'" class="is-paid">
                    {{ $t('calendarDayPopupReservation.paid') }}
                  </span>
                  <span v-else-if="item.payment_status === 'CASH'" class="is-paid">
                    {{ $t('common.ui.paidWithCash') }}
                  </span>
                  <span v-else-if="item.payment_status === 'CARD_REGISTERED'" class="is-paid">
                    {{ $t('calendarDayPopupReservation.cardRegistered') }}
                  </span>
                  <span v-else class="is-not-paid">
                    {{ $t('calendarDayPopupReservation.unpaid') }}
                  </span>
                </small>
              </template>
              <template v-if="item.inquiry_status">
                <br>
                <small>
                  <strong>{{ $t('invoiceDetailsPopup.inquiryStatus') }}:</strong>
                  <span :class="reservationClasses[item.inquiry_status]">{{ reservationStatuses[item.inquiry_status] }}</span>
                </small>
              </template>
            </td>
            <td>{{ item.quantity }}</td>
            <td>{{ item.vat }}</td>
            <td>
              <icon
                v-if="item.billable"
                name="check-circle"
                primaryColor="color-success"
                size="s" />
              <icon
                v-if="!item.billable"
                name="cross-circle"
                primaryColor="color-danger"
                size="s" />
            </td>
            <td>{{ currencyUtils.getQuote(item.item_amount, item.currency) }}</td>
            <td>{{ currencyUtils.getQuote(Number(parseFloat(item.item_amount) + parseFloat(item.vat_amount)).toFixed(2), item.currency) }}</td>
          </tr>
        </tbody>
      </table>

      <btn
        @click.native="close"
        type="popup-full danger">
        {{ $t('common.ui.close') }}
      </btn>
    </div>
  </div>
</template>

<script>
import CurrencyUtils from './../helpers/CurrencyUtils';
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';
import Vue from 'vue';

export default {
  name: 'invoice-details-popup',
  mixins: [
    PopupZindex
  ],
  computed: {
    currencyUtils () {
      return CurrencyUtils;
    },
    paymentMethodName () {
      return {
        'dotpay': this.$t('settingsPayments.paymentMethodDotpay'),
        'paypal': this.$t('settingsPayments.paymentMethodPayPal'),
        'payu': this.$t('settingsPayments.paymentMethodPayU'),
        'przelewy24': this.$t('settingsPayments.paymentMethodPrzelewy24'),
        'tpay': this.$t('settingsPayments.paymentMethodTpay'),
        'paynow': this.$t('settingsPayments.paymentMethodPayNow'),
        'stripe': this.$t('settingsPayments.paymentMethodStripe'),
        'imoje': this.$t('settingsPayments.paymentMethodIMoje'),
        'hotpay': this.$t('settingsPayments.paymentHotPay'),
        'cash': this.$t('common.ui.paidWithCash')
      };
    },
    reservationStatuses () {
      let statuses = {};
      statuses[1] = this.$t('customerReservation.waitsForAccept');
      statuses[3] = this.$t('customerReservation.accepted');
      statuses[4] = this.$t('customerReservation.rejected');
      statuses[5] = this.$t('customerReservation.cancelled');
      statuses[6] = this.$t('customerReservation.waitingList');

      return statuses;
    },
    reservationClasses () {
      let classes = {};
      classes[1] = 'is-neutral';
      classes[3] = 'is-ok';
      classes[4] = 'is-not-ok';
      classes[5] = 'is-not-ok';
      classes[6] = 'is-neutral';

      return classes;
    }
  },
  data () {
    return {
      isVisible: false,
      isLoading: true,
      dataToDisplay: {}
    };
  },
  mounted () {
    this.$bus.$on('invoice-details-popup-display', this.show);
  },
  methods: {
    show (itemID) {
      this.isVisible = true;
      this.loadData(itemID);
      this.increasePopupZindex();
    },
    close () {
      this.isVisible = false;
      this.isLoading = false;
      Vue.set(this, 'dataToDisplay', {});
      this.removePopupZindex();
    },
    loadData (itemID) {
      this.isLoading = true;

      this.$http.get('/panel/invoices/item/' + parseInt(itemID, 10)).then(response => {
        if (response.data && response.data.data) {
          this.dataToDisplay = response.data.data;
        } else {
          this.$bus.$emit('alert-display', {
            title: this.$t('invoiceDetailsPopup.errorMessageTitle'),
            desc: this.$t('invoiceDetailsPopup.errorMessageDesc'),
            okLabel: this.$t('common.ui.ok'),
            okClick: () => true,
            isDanger: true
          });

          this.close();
        }

        this.isLoading = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/invoices/item/' + parseInt(itemID, 10), this.$router, this.$bus, this.loadData.bind(this, itemID));
        this.isLoading = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('invoice-details-popup-display', this.show);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.invoice-details-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 5px 5px rgba(0, 0, 0, .25);
  left: 50%;
  overflow: hidden;
  padding: 2rem 2rem 6rem 2rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-33.333333%);
  transition: transform .25s ease-out;
  width: 56rem;
  will-change: transform;

  &.is-loading {
    .invoice-details-popup-content {
      min-height: 200px;
      position: relative;

      &:after {
        background: #fff;
        content: "";
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }

      &:before {
        animation: spinner 1s infinite linear;
        border: 6px solid $color-text-lightest;
        border-left: 6px solid transparent;
        border-radius: 50%;
        content: "";
        display: block;
        height: 24px;
        left: calc(50% - 12px);
        position: absolute;
        top: calc(50% - 12px);
        width: 24px;
        z-index: 101;
      }
    }
  }

  .invoice-details-popup-content {
    display: flex;
    flex-wrap: wrap;
    margin: 0 0 20px 0;
    padding: 0;

    dt {
      border-top: 1px solid $color-border-light;
      font-weight: bold;
      margin-top: 6px;
      padding-top: 4px;
      width: 40%;

      & + dd {
        border-top: 1px solid $color-border-light;
        margin-top: 6px;
        padding-top: 4px;
      }

      &:first-of-type {
        border-top: none;

        & + dd {
          border-top: none;
        }
      }
    }

    dd {
      margin: 0;
      width: 60%;

      & + dd {
        padding-left: 40%;
        width: 100%;
      }
    }
  }

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;

      .invoice-details-popup {
        transform: translateX(-50%) translateY(-50%);
      }
    }
  }

  h2 {
    font-weight: 400;
    margin: 0 0 2rem 0;
  }

  p {
    color: $color-text;
  }

  .invoice-details-popup-items {
    border-collapse: collapse;
    margin-bottom: 2rem;

    thead {
      td {
        background: $color-bg-light;
        font-size: 11px;
        font-weight: bold;
        padding: 4px;
      }

      td:first-child {
        width: 40%;
      }

      td:nth-child(2),
      td:nth-child(3) {
        text-align: center;
        width: 6%;
      }

      td:nth-child(4) {
        text-align: center;
        width: 16%;
      }

      td:nth-child(5),
      td:nth-child(6) {
        text-align: right;
        width: 16%;
      }
    }

    tbody {
      td {
        border-top: 1px solid $color-bg-medium;
        font-size: 12px;
        padding: 4px;
      }

      td:nth-child(2),
      td:nth-child(3),
      td:nth-child(4) {
        text-align: center;
      }

      td:nth-child(5),
      td:nth-child(6) {
        text-align: right;
      }
    }
  }

  .is-ok,
  .is-paid {
    color: $color-success;
  }

  .is-not-ok,
  .is-not-paid {
    color: $color-danger;
  }
}

@media (max-width: 640px) {
  .invoice-details-popup {
    border-radius: 0;
    height: 100%;
    padding: 2rem 1rem 6rem 1rem;
    transform: translateX(-50%) translateY(-50%);
    width: 100%;
  }
}

@keyframes spinner {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/InvoiceDetailsPopup.vue